/**
 * CsvImportDialog — Importar intérpretes desde un CSV con preview de diferencias.
 *
 * Flujo:
 *   1. Usuario sube CSV (mismo formato que exporta /admin/interpreters/export).
 *   2. Frontend parsea localmente y empareja por `slug` con la lista actual.
 *   3. Calcula el diff por celda y muestra un preview con valores antes → después.
 *   4. Usuario confirma → un único POST /admin/interpreters/bulk-update aplica todo.
 *
 * El backend ya valida con whitelist así que es seguro pasar cualquier columna.
 */
import React, { useMemo, useState } from 'react';
import { X, Upload, Loader2, AlertCircle, CheckCircle2, FileText, ArrowRight } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

// Mismas columnas escribibles que el endpoint bulk-update. Cualquier otra
// columna del CSV se ignora (read-only). Las listas se serializan con " | ".
const WRITABLE_FIELDS = [
  'full_name', 'apellido_paterno', 'apellido_materno', 'nombres',
  'email', 'phone', 'city',
  'additional_emails', 'additional_phones',
  'active_languages', 'passive_languages', 'languages',
  'specialty', 'modalities', 'language_pairs',
  'bio', 'references_text', 'linkedin_url', 'website',
  'is_active', 'is_listed_in_directory', 'data_verified',
  'paid_until', 'membership_status', 'membership_notes',
];

const LIST_FIELDS = new Set([
  'active_languages', 'passive_languages', 'languages', 'specialty', 'modalities', 'language_pairs',
  'additional_emails', 'additional_phones',
]);

const BOOL_FIELDS = new Set([
  'is_active', 'is_listed_in_directory', 'data_verified',
]);

// Text fields that may legitimately contain trailing whitespace, newlines,
// or other "noise" coming from the DB. We treat them as equivalent if their
// trimmed values match, since the CSV exporter outputs `str(v)` and the
// reader trims by default.
function textEqual(a, b) {
  const sa = (a === null || a === undefined) ? '' : String(a).trim();
  const sb = (b === null || b === undefined) ? '' : String(b).trim();
  return sa === sb;
}

// Minimal RFC-4180 CSV parser (supports quoted fields with embedded commas
// and double-quotes). No external dep. Good enough for our exports.
function parseCsv(text) {
  // Strip BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const rows = [];
  let i = 0, field = '', row = [], inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  if (!rows.length) return { headers: [], rows: [] };
  const headers = rows.shift().map((h) => h.trim());
  const data = rows
    .filter((r) => r.some((c) => c && c.trim()))   // skip blank lines
    .map((r) => {
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = (r[idx] ?? '').trim(); });
      return obj;
    });
  return { headers, rows: data };
}

function parseList(v) {
  if (!v) return [];
  return String(v).split('|').map((x) => x.trim()).filter(Boolean);
}

function parseBool(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'sí' || s === 'si' || s === 'yes';
}

function arraysEqual(a, b) {
  // Normalize both sides: drop falsy entries, trim, lowercase-trim-free compare.
  const norm = (v) => (Array.isArray(v) ? v : []).map((x) => String(x).trim()).filter(Boolean);
  const na = norm(a);
  const nb = norm(b);
  if (na.length !== nb.length) return false;
  return na.every((x, i) => x === nb[i]);
}

function displayVal(field, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(' | ');
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
}

export default function CsvImportDialog({ open, onClose, rows, onApplied }) {
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [csvRows, setCsvRows] = useState([]);     // parsed objects
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);     // bulk-update response

  // index existing by slug
  const bySlug = useMemo(() => {
    const m = new Map();
    for (const r of rows || []) m.set(r.slug, r);
    return m;
  }, [rows]);

  // Diff structure: [{slug, full_name, status, changes:[{field, before, after}]}]
  const diff = useMemo(() => {
    const out = [];
    for (const row of csvRows) {
      const slug = row.slug;
      if (!slug) { out.push({ slug: '(sin slug)', status: 'invalid', error: 'Fila sin columna slug', changes: [] }); continue; }
      const orig = bySlug.get(slug);
      if (!orig) { out.push({ slug, status: 'missing', error: 'Intérprete no encontrado en la base', changes: [] }); continue; }
      const changes = [];
      for (const f of WRITABLE_FIELDS) {
        if (!(f in row)) continue;                // header missing → skip
        const raw = row[f];
        let newVal;
        let same;
        if (LIST_FIELDS.has(f)) {
          newVal = parseList(raw);
          same = arraysEqual(newVal, orig[f]);
        } else if (BOOL_FIELDS.has(f)) {
          newVal = parseBool(raw);
          same = Boolean(newVal) === Boolean(orig[f]);
        } else {
          newVal = (raw ?? '').toString().trim();
          same = textEqual(newVal, orig[f]);
        }
        if (!same) {
          changes.push({ field: f, before: orig[f], after: newVal });
        }
      }
      out.push({
        slug,
        full_name: orig.full_name,
        status: changes.length ? 'changes' : 'unchanged',
        changes,
      });
    }
    return out;
  }, [csvRows, bySlug]);

  const summary = useMemo(() => {
    const s = { withChanges: 0, unchanged: 0, missing: 0, invalid: 0, totalChanges: 0 };
    for (const d of diff) {
      if (d.status === 'changes') { s.withChanges++; s.totalChanges += d.changes.length; }
      else if (d.status === 'unchanged') s.unchanged++;
      else if (d.status === 'missing') s.missing++;
      else if (d.status === 'invalid') s.invalid++;
    }
    return s;
  }, [diff]);

  function reset() {
    setFileName(''); setParseError(''); setCsvRows([]); setCsvHeaders([]); setResult(null);
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError(''); setResult(null);
    try {
      const text = await file.text();
      const { headers, rows: parsed } = parseCsv(text);
      if (!headers.includes('slug')) {
        setParseError('El CSV debe tener una columna "slug" (es la que identifica al intérprete).');
        setCsvRows([]); setCsvHeaders([]); return;
      }
      setCsvHeaders(headers);
      setCsvRows(parsed);
    } catch (err) {
      setParseError(`No se pudo leer el archivo: ${err.message || err}`);
    } finally {
      e.target.value = '';
    }
  }

  async function apply() {
    const changesPayload = diff
      .filter((d) => d.status === 'changes')
      .map((d) => ({
        slug: d.slug,
        changes: Object.fromEntries(d.changes.map((c) => [c.field, c.after])),
      }));
    if (!changesPayload.length) {
      toast.info('No hay cambios para aplicar');
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post('/admin/interpreters/bulk-update', { changes: changesPayload });
      setResult(data);
      if (data.failed === 0) {
        toast.success(`Importadas ${data.ok} fila(s) sin errores`);
      } else {
        toast.warning(`${data.ok} aplicadas, ${data.failed} con errores`);
      }
      onApplied?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al aplicar el CSV');
    } finally {
      setApplying(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-6" onClick={onClose} data-testid="csv-import-dialog">
      <div className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col sm:max-h-[92vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-charcoal-700">
            <Upload size={16} className="text-emerald-600" />
            Importar intérpretes desde CSV
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-charcoal-500" data-testid="csv-import-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Step 1: pick file */}
          {!csvRows.length && (
            <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/40">
              <FileText size={36} className="mx-auto text-charcoal-300 mb-3" />
              <h3 className="text-sm font-semibold text-charcoal-800 mb-2">Sube el CSV revisado</h3>
              <p className="text-xs text-charcoal-500 mb-4 max-w-md mx-auto leading-relaxed">
                Usa el mismo formato que exporta el botón <strong>Descargar CSV</strong>. La columna <code className="bg-white px-1 py-0.5 rounded border border-gray-200">slug</code> es obligatoria — identifica a cada intérprete. Las listas (idiomas, especialidades) van separadas por <code className="bg-white px-1 py-0.5 rounded border border-gray-200">|</code>.
              </p>
              <label className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-600 cursor-pointer">
                <Upload size={14} /> Seleccionar archivo CSV
                <input type="file" accept=".csv,text/csv" onChange={onFile} hidden data-testid="csv-import-file-input" />
              </label>
              {parseError && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                  <AlertCircle size={12} /> {parseError}
                </div>
              )}
            </div>
          )}

          {/* Step 2: preview diff */}
          {csvRows.length > 0 && !result && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-charcoal-700">
                  <strong className="text-emerald-700">{fileName}</strong> — {csvRows.length} fila(s)
                </div>
                <button onClick={reset} className="text-xs font-medium text-charcoal-600 hover:text-charcoal-900 underline">
                  Cargar otro archivo
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="csv-import-summary">
                <SummaryCard label="Con cambios" value={summary.withChanges} accent="amber" />
                <SummaryCard label="Sin cambios" value={summary.unchanged} accent="gray" />
                <SummaryCard label="No encontrados" value={summary.missing} accent="rose" />
                <SummaryCard label="Total ediciones" value={summary.totalChanges} accent="emerald" />
              </div>

              {summary.missing > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    {summary.missing} fila(s) tienen un <code className="bg-white px-1 rounded">slug</code> que no existe en la base actual.
                    Estas filas se ignorarán. Si quieres CREAR intérpretes nuevos, usa el flujo de creación individual o invitaciones por email.
                  </div>
                </div>
              )}

              {/* Diff list */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-charcoal-500 border-b border-gray-200">
                  Vista previa de cambios
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-gray-100" data-testid="csv-import-diff-list">
                  {diff.map((d, idx) => <DiffRow key={`${d.slug}-${idx}`} item={d} />)}
                </div>
              </div>
            </>
          )}

          {/* Step 3: result */}
          {result && (
            <div className="bg-white border border-emerald-200 rounded-xl p-5 text-center" data-testid="csv-import-result">
              <CheckCircle2 size={36} className="text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-charcoal-800 mb-1">Importación completa</h3>
              <p className="text-sm text-charcoal-600 mb-4">
                {result.ok} fila(s) actualizadas correctamente
                {result.failed > 0 && <>, <span className="text-rose-700 font-semibold">{result.failed} con error</span></>}.
              </p>
              {result.failed > 0 && (
                <ul className="text-left text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 list-disc ml-6 max-h-40 overflow-y-auto">
                  {result.results.filter((r) => !r.ok).map((r) => (
                    <li key={r.slug}>{r.slug}: {r.error}</li>
                  ))}
                </ul>
              )}
              <button onClick={onClose} className="mt-4 bg-cmic-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-600">
                Cerrar
              </button>
            </div>
          )}
        </div>

        {/* Sticky footer with action button (only in step 2) */}
        {csvRows.length > 0 && !result && (
          <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-charcoal-600 hover:bg-gray-100 rounded-lg" disabled={applying}>
              Cancelar
            </button>
            <button
              onClick={apply}
              disabled={applying || summary.withChanges === 0}
              data-testid="csv-import-apply-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Aplicar {summary.withChanges > 0 ? `${summary.withChanges} cambio${summary.withChanges === 1 ? '' : 's'}` : 'cambios'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const ACCENT = {
    amber:   'bg-amber-50 border-amber-200 text-amber-700',
    gray:    'bg-gray-50 border-gray-200 text-charcoal-600',
    rose:    'bg-rose-50 border-rose-200 text-rose-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`border rounded-xl p-3 text-center ${ACCENT[accent] || ACCENT.gray}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] font-medium uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function DiffRow({ item }) {
  if (item.status === 'unchanged') {
    return (
      <div className="px-3 py-2 flex items-center gap-2 text-xs text-charcoal-400 bg-gray-50/40">
        <span className="font-mono">{item.slug}</span>
        <span className="ml-auto italic">Sin cambios</span>
      </div>
    );
  }
  if (item.status === 'missing' || item.status === 'invalid') {
    return (
      <div className="px-3 py-2 flex items-center gap-2 text-xs text-rose-700 bg-rose-50/40">
        <AlertCircle size={11} />
        <span className="font-mono">{item.slug}</span>
        <span className="ml-auto italic">{item.error}</span>
      </div>
    );
  }
  return (
    <div className="px-3 py-2 bg-amber-50/40">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-semibold text-charcoal-800">
          {item.full_name} <span className="text-charcoal-400 font-mono font-normal">({item.slug})</span>
        </div>
        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
          {item.changes.length} cambio{item.changes.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-1 pl-2 border-l-2 border-amber-300">
        {item.changes.map((c, i) => (
          <div key={i} className="text-[11px] flex items-start gap-2 flex-wrap">
            <span className="font-medium text-charcoal-600 min-w-[100px]">{c.field}:</span>
            <span className="text-charcoal-500 line-through" title="Antes">{displayVal(c.field, c.before)}</span>
            <ArrowRight size={10} className="text-charcoal-400 mt-0.5" />
            <span className="text-emerald-700 font-medium" title="Después">{displayVal(c.field, c.after)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
